import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useWallet, useConnection } from '@solana/wallet-adapter-react';
import { PublicKey, LAMPORTS_PER_SOL, SystemProgram, Transaction } from '@solana/web3.js';
import { Buffer } from 'buffer';
import { supabase } from '../supabaseClient';
import { useAdmin } from '../contexts/AdminContext';
import { 
  buildVoteInstruction,
  buildVoteWithStatsInstruction,
  createTransaction, 
  WALLETS,
  fetchAllPopperNFTs,
  fetchNFTMetadata,
  PROGRAM_ID,
} from '../utils/popperverseProgram';
import {
  DISCRIMINATORS,
  PDA_SEEDS,
  VOTING_ROUND_LAYOUT,
} from '../constants/accountLayouts';
import NFTStatsCard, { readStatsFromReserved } from '../components/NFTStatsCard';
import './Vote.css';

const Vote = () => {
  const { publicKey, sendTransaction } = useWallet();
  const { connection } = useConnection();
  const { isAdmin } = useAdmin();
  const [votes, setVotes] = useState({});
  const [totalSOL, setTotalSOL] = useState({});
  const [voteAmount, setVoteAmount] = useState(0.1);
  const [isVoting, setIsVoting] = useState(false);
  const [countdown, setCountdown] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  
  const [votingEnded, setVotingEnded] = useState(false);
  const [votingRound, setVotingRound] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [isLoadingCandidates, setIsLoadingCandidates] = useState(true);
  
  // Admin panel state
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [newRoundNumber, setNewRoundNumber] = useState('');
  const [roundDuration, setRoundDuration] = useState('60'); // minutes
  const [isProcessing, setIsProcessing] = useState(false);
  const [adminLogs, setAdminLogs] = useState([]);
  const [allRounds, setAllRounds] = useState([]);
  const [selectedRound, setSelectedRound] = useState(null);
  const [isLoadingRounds, setIsLoadingRounds] = useState(false);
  const [promoteNftMint, setPromoteNftMint] = useState('');
  const [promoteToRound, setPromoteToRound] = useState('');
  
  // Modal state for viewing Popper stats
  const [selectedPopper, setSelectedPopper] = useState(null);
  const [showStatsModal, setShowStatsModal] = useState(false);
  
  // Accordion state for leaderboard rounds
  const [expandedRound, setExpandedRound] = useState(null);
  
  // Rating modal state (for voting with stats)
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [ratingCandidate, setRatingCandidate] = useState(null);
  const [ratings, setRatings] = useState({
    cuteness: 5,
    creativity: 5,
    popPower: 5,
    originality: 5,
    potential: 5,
  });

  // Vote wallet from environment variable (hot wallet)
  const VOTE_WALLET = WALLETS.VOTE;

  // State for loading round from chain
  const [isLoadingRound, setIsLoadingRound] = useState(true);
  const [noActiveRound, setNoActiveRound] = useState(false);

  // Fetch candidates from blockchain (PopperNFT accounts)
  useEffect(() => {
    const loadCandidatesFromChain = async () => {
      if (!connection) return;
      
      setIsLoadingCandidates(true);
      
      try {
        console.log('[Vote] Fetching PopperNFT accounts from chain...');
        
        // Fetch all voting-eligible PopperNFTs
        const popperNfts = await fetchAllPopperNFTs(connection, { votingEligibleOnly: true });
        console.log(`[Vote] Found ${popperNfts.length} voting-eligible NFTs`);
        
        if (popperNfts.length === 0) {
          console.log('[Vote] No candidates yet');
          setCandidates([]);
          setIsLoadingCandidates(false);
          return;
        }
        
        // Fetch metadata for each NFT
        const candidatesWithMetadata = await Promise.all(
          popperNfts.map(async (nft) => {
            const metadata = await fetchNFTMetadata(connection, nft.nftMint);
            
            // Parse stats from reserved bytes if available
            console.log('[Vote] NFT reserved bytes available:', nft.nftMint.slice(0, 12), 'reserved length:', nft.reserved?.length);
            const stats = nft.reserved ? readStatsFromReserved(nft.reserved) : null;
            console.log('[Vote] Parsed stats for', nft.nftMint.slice(0, 12), ':', stats);
            
            return {
              id: nft.nftMint,
              pda: nft.pda,
              name: metadata?.name || `Popper #${nft.nftMint.slice(0, 8)}`,
              description: metadata?.description || 'Community-created Popper design',
              image: metadata?.image || '/Design/silhouette.png',
              creatorWallet: nft.creatorWallet,
              totalVotes: nft.totalVotes,
              totalVoteSol: nft.totalVoteSol,
              createdAt: nft.createdAt,
              createdInRound: nft.createdInRound,
              lastActiveRound: nft.lastActiveRound,  // Include lastActiveRound for eligibility check
              stats: stats?.averages || null,  // Include stats averages for slider initialization
            };
          })
        );
        
        console.log('[Vote] Candidates loaded with metadata:', candidatesWithMetadata);
        setCandidates(candidatesWithMetadata);
        
        // Initialize vote counts from chain data
        const initialVotes = {};
        const initialSOL = {};
        candidatesWithMetadata.forEach(c => {
          initialVotes[c.id] = c.totalVotes;
          initialSOL[c.id] = c.totalVoteSol / LAMPORTS_PER_SOL;
        });
        setVotes(initialVotes);
        setTotalSOL(initialSOL);
        
      } catch (error) {
        console.error('[Vote] Failed to load candidates from chain:', error);
      } finally {
        setIsLoadingCandidates(false);
      }
    };
    
    loadCandidatesFromChain();
  }, [connection]);

  useEffect(() => {
    loadVotesFromStorage();
    
    // Don't start countdown until we have a valid voting round config with timestamps
    if (!votingRound || !votingRound.endTimestamp) return;
    
    // Calculate end date from config
    const endTimestampMs = votingRound.endTimestamp * 1000;  // Convert Unix seconds to milliseconds
    
    // Function to calculate and update countdown
    const updateCountdown = () => {
      const now = Date.now();
      const distance = endTimestampMs - now;

      if (distance < 0) {
        setVotingEnded(true);
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return false; // Signal to clear interval
      } else {
        setVotingEnded(false);
        const days = Math.floor(distance / (1000 * 60 * 60 * 24));
        const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((distance % (1000 * 60)) / 1000);
        
        setCountdown({ days, hours, minutes, seconds });
        return true; // Continue interval
      }
    };
    
    // Calculate immediately on mount/update
    updateCountdown();
    
    // Update countdown every second
    const timer = setInterval(() => {
      if (!updateCountdown()) {
        clearInterval(timer);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [votingRound]);

  // Modal is open state tracking (no scroll lock - let user scroll to see modal)
  useEffect(() => {
    // No scroll locking - modal uses position:fixed and will be visible
  }, [showStatsModal, showRatingModal]);

  const loadVotesFromStorage = () => {
    const storedVotes = localStorage.getItem('popperverseVotes');
    const storedSOL = localStorage.getItem('popperverseSOL');
    
    if (storedVotes) {
      setVotes(JSON.parse(storedVotes));
    }
    if (storedSOL) {
      setTotalSOL(JSON.parse(storedSOL));
    }
  };

  const saveVotesToStorage = (newVotes, newSOL) => {
    localStorage.setItem('popperverseVotes', JSON.stringify(newVotes));
    localStorage.setItem('popperverseSOL', JSON.stringify(newSOL));
  };

  const handleVote = async (design) => {
    if (!publicKey) {
      alert('Please connect your wallet first!');
      return;
    }

    if (voteAmount <= 0) {
      alert('Please enter a valid vote amount!');
      return;
    }

    // Find the candidate info
    const designInfo = voteOptions.find(opt => opt.id === design);
    if (!designInfo) {
      alert('Could not find candidate information');
      return;
    }
    
    // Open the rating modal instead of voting directly
    setRatingCandidate(designInfo);
    
    // Initialize sliders from current NFT stats averages, or default to 5
    const currentStats = designInfo.stats;
    console.log('[Vote] Opening rating modal for:', designInfo.name);
    console.log('[Vote] designInfo.stats:', currentStats);
    console.log('[Vote] Full designInfo:', designInfo);
    
    if (currentStats && (currentStats.cuteness > 0 || currentStats.creativity > 0 || currentStats.popPower > 0 || currentStats.originality > 0 || currentStats.potential > 0)) {
      // Round to nearest integer for slider (stats are 0-10 with decimals)
      console.log('[Vote] Using existing stats for sliders');
      setRatings({
        cuteness: Math.round(currentStats.cuteness) || 5,
        creativity: Math.round(currentStats.creativity) || 5,
        popPower: Math.round(currentStats.popPower) || 5,
        originality: Math.round(currentStats.originality) || 5,
        potential: Math.round(currentStats.potential) || 5,
      });
    } else {
      // No stats yet, default to 5
      console.log('[Vote] No stats found, defaulting to 5');
      setRatings({ cuteness: 5, creativity: 5, popPower: 5, originality: 5, potential: 5 });
    }
    setShowRatingModal(true);
  };
  
  // Submit vote with ratings
  const handleSubmitVoteWithRatings = async () => {
    if (!ratingCandidate || !publicKey) return;

    setIsVoting(true);
    setShowRatingModal(false);

    try {
      const design = ratingCandidate.id;
      const designName = ratingCandidate.name || `Popper ${design.slice(0, 8)}`;
      
      // The NFT mint address
      const nftMint = new PublicKey(design);

      const amountLamports = Math.floor(voteAmount * LAMPORTS_PER_SOL);
      
      // Use the ACTIVE voting round number
      const activeRoundNumber = votingRound?.roundNumber || 1;
      
      // Convert ratings to array format [cuteness, creativity, popPower, originality, potential]
      const ratingsArray = [
        ratings.cuteness,
        ratings.creativity,
        ratings.popPower,
        ratings.originality,
        ratings.potential,
      ];

      // Build the vote WITH STATS instruction using the Popperverse program
      const voteIx = buildVoteWithStatsInstruction({
        voter: publicKey,
        nftMint,
        voteWallet: VOTE_WALLET,
        roundNumber: activeRoundNumber,
        amountLamports,
        ratings: ratingsArray,
      });

      // Create and send transaction
      const transaction = createTransaction(voteIx);

      // Get latest blockhash
      const { blockhash } = await connection.getLatestBlockhash();
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = publicKey;

      // Simulate first to get detailed error
      console.log('[Vote] Simulating transaction...');
      try {
        const simulation = await connection.simulateTransaction(transaction);
        if (simulation.value.err) {
          console.error('[Vote] Simulation error:', simulation.value.err);
          console.error('[Vote] Simulation logs:', simulation.value.logs);
          throw new Error(`Transaction simulation failed: ${JSON.stringify(simulation.value.err)}\nLogs: ${simulation.value.logs?.join('\n')}`);
        }
        console.log('[Vote] Simulation passed');
      } catch (simError) {
        console.error('[Vote] Simulation exception:', simError);
        throw simError;
      }

      // Send transaction
      const signature = await sendTransaction(transaction, connection);

      // Wait for confirmation
      await connection.confirmTransaction(signature, 'confirmed');

      // Log to Supabase (optional - may fail if schema doesn't match)
      try {
        const { error: dbError } = await supabase
          .from('votes')
          .insert({
            wallet_address: publicKey.toString(),
            design_id: design,
            design_name: designName,
            vote_amount_sol: voteAmount,
            transaction_signature: signature,
            destination_wallet: VOTE_WALLET.toString(),
            // Include ratings in the log
            ratings: ratingsArray,
          });

        if (dbError) {
          console.error('Failed to log vote to Supabase:', dbError);
        } else {
          console.log('Vote with ratings successfully logged to Supabase');
        }
      } catch (dbError) {
        console.error('Supabase logging error:', dbError);
      }

      // Update vote counts
      const newVotes = {
        ...votes,
        [design]: (votes[design] || 0) + 1
      };
      const newSOL = {
        ...totalSOL,
        [design]: (totalSOL[design] || 0) + voteAmount
      };

      setVotes(newVotes);
      setTotalSOL(newSOL);
      saveVotesToStorage(newVotes, newSOL);

      alert(`Successfully voted for ${designName}! You spent ${voteAmount} SOL.\n\nRatings submitted:\n🥰 Cuteness: ${ratings.cuteness}\n🎨 Creativity: ${ratings.creativity}\n💥 Pop Power: ${ratings.popPower}\n✨ Originality: ${ratings.originality}\n🚀 Potential: ${ratings.potential}\n\nTransaction: ${signature}`);
    } catch (error) {
      console.error('Voting error:', error);
      alert(`Voting failed: ${error.message}`);
    } finally {
      setIsVoting(false);
      setRatingCandidate(null);
    }
  };
  
  // Handle clicking on a candidate card to view stats
  const handleViewStats = (candidate) => {
    setSelectedPopper(candidate);
    setShowStatsModal(true);
  };

  /*
   * CANDIDATE FORMAT TEMPLATE
   * -------------------------
   * Candidates are loaded from public/candidates.json
   * Each candidate should have this structure:
   * 
   * {
   *   "id": 1,                                    // Unique numeric ID
   *   "name": "Example Popper",                   // Display name
   *   "image": "/Design/Candidates/example.png", // Path to image in public folder
   *   "frame": "orange",                         // Frame color: "orange" or "blue"
   *   "nftMint": "ABC123...",                    // (Optional) On-chain NFT mint address
   *   "creator": "XYZ789...",                    // (Optional) Creator wallet address
   *   "createdAt": "2025-12-09T00:00:00Z"        // (Optional) Creation timestamp
   * }
   * 
   * To add candidates:
   * 1. Via Create page admin mode (manual image upload)
   * 2. Via NFT minting flow (automatic)
   * 3. Manually edit public/candidates.json
   */

  // Use dynamically loaded candidates, filtered by eligibility for active round
  const voteOptions = useMemo(() => {
    // If no active round, return empty (we'll show leaderboard instead)
    if (!votingRound?.roundNumber || noActiveRound) return [];
    
    const activeRound = votingRound.roundNumber;
    
    // Filter to only show NFTs eligible for current round:
    // - NFT was created in this round, OR
    // - NFT was promoted to this round (lastActiveRound matches)
    return candidates.filter(candidate => {
      const isEligible = 
        candidate.createdInRound === activeRound || 
        candidate.lastActiveRound === activeRound;
      
      if (!isEligible) {
        console.log(`[Vote] NFT ${candidate.id.slice(0, 12)}... not eligible for round ${activeRound} (createdIn: ${candidate.createdInRound}, lastActive: ${candidate.lastActiveRound})`);
      }
      
      return isEligible;
    });
  }, [candidates, votingRound?.roundNumber, noActiveRound]);
  
  // Get leaderboard for most recent completed round
  const leaderboardData = useMemo(() => {
    if (!allRounds || allRounds.length === 0) return null;
    
    // Find the most recent completed round (not round 0, and has winner OR is ended/inactive)
    const completedRound = allRounds
      .filter(r => r.roundNumber > 0 && (r.hasWinner || r.isEnded || !r.isActive))
      .sort((a, b) => b.roundNumber - a.roundNumber)[0];
    
    if (!completedRound) return null;
    
    console.log('[Vote] leaderboardData - using completed round:', completedRound.roundNumber, 'hasWinner:', completedRound.hasWinner);
    
    // Get candidates from that round only - no fallbacks to avoid showing round 0 candidates
    const roundCandidates = candidates
      .filter(c => 
        c.createdInRound === completedRound.roundNumber || 
        c.lastActiveRound === completedRound.roundNumber
      )
      .sort((a, b) => {
        const solDiff = Number(b.totalVoteSol || 0) - Number(a.totalVoteSol || 0);
        if (solDiff !== 0) return solDiff;
        return Number(b.totalVotes) - Number(a.totalVotes);
      });
    
    console.log('[Vote] leaderboardData - found', roundCandidates.length, 'candidates for round', completedRound.roundNumber);
    
    return {
      round: completedRound,
      candidates: roundCandidates
    };
  }, [allRounds, candidates]);
  
  // Get all completed rounds with their candidates for the accordion
  const completedRoundsData = useMemo(() => {
    if (!allRounds || allRounds.length === 0) return [];
    
    // Get all completed rounds (not round 0, and either has winner, ended, or inactive)
    const completedRounds = allRounds
      .filter(r => r.roundNumber > 0 && (r.hasWinner || r.isEnded || !r.isActive))
      .sort((a, b) => b.roundNumber - a.roundNumber); // Newest first
    
    return completedRounds.map(round => {
      // Get candidates for this round - only show candidates explicitly in this round
      let roundCandidates = candidates
        .filter(c => 
          c.createdInRound === round.roundNumber || 
          c.lastActiveRound === round.roundNumber
        )
        .sort((a, b) => {
          const solDiff = Number(b.totalVoteSol || 0) - Number(a.totalVoteSol || 0);
          if (solDiff !== 0) return solDiff;
          return Number(b.totalVotes) - Number(a.totalVotes);
        });
      
      return {
        round,
        candidates: roundCandidates
      };
    });
  }, [allRounds, candidates]);

  // Admin logging helper
  const addLog = useCallback((message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setAdminLogs(prev => [...prev, { message, type, timestamp }]);
  }, []);

  // Instruction discriminators from centralized constants (memoized to avoid recreation)
  const ADMIN_DISCRIMINATORS = useMemo(() => ({
    initialize_voting_round: Buffer.from(DISCRIMINATORS.INSTRUCTIONS.INITIALIZE_VOTING_ROUND),
    close_voting_round: Buffer.from(DISCRIMINATORS.INSTRUCTIONS.CLOSE_VOTING_ROUND),
    declare_winner: Buffer.from(DISCRIMINATORS.INSTRUCTIONS.DECLARE_WINNER),
    archive_nft: Buffer.from(DISCRIMINATORS.INSTRUCTIONS.ARCHIVE_NFT),
    promote_to_next_round: Buffer.from(DISCRIMINATORS.INSTRUCTIONS.PROMOTE_TO_NEXT_ROUND),
    force_end_round: Buffer.from(DISCRIMINATORS.INSTRUCTIONS.FORCE_END_ROUND),
  }), []);

  // Get VotingRound PDA
  const getVotingRoundPDA = useCallback((roundNumber) => {
    const VOTING_ROUND_SEED = Buffer.from(PDA_SEEDS.VOTING_ROUND);
    return PublicKey.findProgramAddressSync(
      [VOTING_ROUND_SEED, Buffer.from([roundNumber])],
      PROGRAM_ID
    )[0];
  }, []);

  // Get PopperNFT PDA
  const getPopperNFTPDA = useCallback((nftMint) => {
    const POPPER_NFT_SEED = Buffer.from(PDA_SEEDS.POPPER_NFT);
    const mintPubkey = typeof nftMint === 'string' ? new PublicKey(nftMint) : nftMint;
    return PublicKey.findProgramAddressSync(
      [POPPER_NFT_SEED, mintPubkey.toBuffer()],
      PROGRAM_ID
    )[0];
  }, []);

  // Fetch all voting rounds from chain (batch fetch rounds 1-20)
  const fetchAllRounds = useCallback(async () => {
    if (!connection) return;
    
    setIsLoadingRounds(true);
    const rounds = [];
    
    try {
      // Derive all PDAs first, then batch fetch
      const pdas = [];
      for (let i = 1; i <= 20; i++) {
        pdas.push(getVotingRoundPDA(i));
      }
      
      const accounts = await connection.getMultipleAccountsInfo(pdas);
      
      for (let i = 0; i < accounts.length; i++) {
        const info = accounts[i];
        if (!info) continue;
        
        const pda = pdas[i];
        const data = info.data;
          
          const roundNumber = data[8];
          const startTimestamp = Number(data.readBigInt64LE(9));
          const endTimestamp = Number(data.readBigInt64LE(17));
          const hasWinner = data[25] === 1;
          const winnerMint = hasWinner ? new PublicKey(data.slice(26, 58)).toBase58() : null;
          
          const postWinnerOffset = hasWinner ? 58 : 26;
          
          const totalNftsCreated = Number(data.readBigUInt64LE(postWinnerOffset));
          const totalVotesCast = Number(data.readBigUInt64LE(postWinnerOffset + 8));
          const totalSolVoted = Number(data.readBigUInt64LE(postWinnerOffset + 16));
          const totalPoppersVoted = Number(data.readBigUInt64LE(postWinnerOffset + 24));
          const poppersSwapEnabled = data[postWinnerOffset + 32] === 1;
          const isActiveOnChain = data[postWinnerOffset + 33] === 1;
          
          const now = Date.now() / 1000;
          const isEnded = now >= endTimestamp || !isActiveOnChain;
          
          rounds.push({
            roundNumber,
            pda: pda.toBase58(),
            startTimestamp,
            endTimestamp,
            startDate: new Date(startTimestamp * 1000).toISOString(),
            endDate: new Date(endTimestamp * 1000).toISOString(),
            isActive: isActiveOnChain,
            isEnded,
            hasWinner,
            winnerMint,
            totalNftsCreated,
            totalVotesCast,
          });
      }
      
      // Sort by round number descending (newest first)
      rounds.sort((a, b) => b.roundNumber - a.roundNumber);
      setAllRounds(rounds);
      
      // Auto-select the first (most recent) round if none selected
      if (rounds.length > 0 && !selectedRound) {
        setSelectedRound(rounds[0]);
      }
      
    } catch (error) {
      console.error('Error fetching rounds:', error);
    } finally {
      setIsLoadingRounds(false);
    }
  }, [connection, getVotingRoundPDA, selectedRound]);

  // Fetch rounds when admin panel is opened OR when no active round (to show leaderboard)
  useEffect(() => {
    if (showAdminPanel && isAdmin) {
      fetchAllRounds();
    }
  }, [showAdminPanel, isAdmin, fetchAllRounds]);
  
  // Also fetch all rounds when we detect no active round (to show previous results)
  useEffect(() => {
    if (noActiveRound && allRounds.length === 0) {
      console.log('[Vote] No active round - fetching all rounds for leaderboard');
      fetchAllRounds();
    }
  }, [noActiveRound, allRounds.length, fetchAllRounds]);

  // Fetch active voting round from blockchain on page load
  useEffect(() => {
    const loadActiveRoundFromChain = async () => {
      if (!connection) return;
      
      setIsLoadingRound(true);
      
      try {
        console.log('[Vote] Fetching active voting round from chain...');
        
        // Derive all PDAs first, then batch fetch
        const pdas = [];
        for (let i = 1; i <= 20; i++) {
          pdas.push(getVotingRoundPDA(i));
        }
        
        const accounts = await connection.getMultipleAccountsInfo(pdas);
        
        for (let i = 0; i < accounts.length; i++) {
          const info = accounts[i];
          if (!info) continue;
          
          const pda = pdas[i];
          const data = info.data;
            const roundNumber = data[8];
            const startTimestamp = Number(data.readBigInt64LE(9));
            const endTimestamp = Number(data.readBigInt64LE(17));
            const hasWinner = data[25] === 1;
            const winnerMint = hasWinner ? new PublicKey(data.slice(26, 58)).toBase58() : null;
            
            // Dynamic offset calculation
            const postWinnerOffset = hasWinner ? 58 : 26;
            const isActiveOnChain = data[postWinnerOffset + 33] === 1;
            
            const now = Date.now() / 1000;
            const isEnded = now >= endTimestamp || !isActiveOnChain;
            
            // If this round is active ON-CHAIN, use it
            if (isActiveOnChain) {
              console.log(`[Vote] Found active round ${roundNumber}`);
              setVotingRound({
                roundNumber,
                pda: pda.toBase58(),
                startTimestamp,
                endTimestamp,
                isActive: true,
                hasWinner,
                winnerMint,
              });
              setVotingEnded(false);
              setNoActiveRound(false);
              setIsLoadingRound(false);
              return;
            }
            
            // Track the most recent round (even if ended)
            if (i === 0 || !votingRound) {
              setVotingRound({
                roundNumber,
                pda: pda.toBase58(),
                startTimestamp,
                endTimestamp,
                isActive: false,
                hasWinner,
                winnerMint,
              });
              if (isEnded || !isActiveOnChain) {
                setVotingEnded(true);
              }
            }
        }
        
        // If we get here, no active round found
        console.log('[Vote] No active voting round found on chain');
        if (!votingRound || !votingRound.roundNumber) {
          setNoActiveRound(true);
        }
        
      } catch (error) {
        console.error('[Vote] Failed to fetch voting round from chain:', error);
      } finally {
        setIsLoadingRound(false);
      }
    };
    
    loadActiveRoundFromChain();
  }, [connection, getVotingRoundPDA]);

  // Initialize a new voting round
  const handleInitializeRound = async () => {
    console.log('[Vote] handleInitializeRound called, publicKey:', publicKey?.toBase58());
    
    if (!publicKey) {
      alert('Please connect your wallet first!');
      return;
    }

    const roundNum = parseInt(newRoundNumber);
    console.log('[Vote] Round number:', roundNum, 'from input:', newRoundNumber);
    if (isNaN(roundNum) || roundNum < 1 || roundNum > 255) {
      alert('Please enter a valid round number (1-255)');
      return;
    }

    const durationMins = parseInt(roundDuration);
    console.log('[Vote] Duration mins:', durationMins, 'from input:', roundDuration);
    if (isNaN(durationMins) || durationMins < 1) {
      alert('Please enter a valid duration in minutes');
      return;
    }

    setIsProcessing(true);
    setAdminLogs([]);
    addLog(`Initializing Voting Round ${roundNum}...`);
    console.log('[Vote] Starting initialization...');

    try {
      const durationSeconds = durationMins * 60;
      const votingRoundPDA = getVotingRoundPDA(roundNum);
      addLog(`VotingRound PDA: ${votingRoundPDA.toBase58().slice(0, 16)}...`);

      // Derive program_authority PDA
      const PROGRAM_AUTHORITY_SEED = Buffer.from('program_authority');
      const [programAuthorityPda] = PublicKey.findProgramAddressSync(
        [PROGRAM_AUTHORITY_SEED],
        PROGRAM_ID
      );
      addLog(`Program Authority PDA: ${programAuthorityPda.toBase58().slice(0, 16)}...`);

      // Build the initialize_voting_round instruction
      // IDL structure: { round_number: u8, duration_seconds: i64 }
      const roundNumberBuf = Buffer.alloc(1);
      roundNumberBuf.writeUInt8(roundNum);
      const durationBuf = Buffer.alloc(8);
      // eslint-disable-next-line no-undef
      durationBuf.writeBigInt64LE(BigInt(durationSeconds));
      
      const instructionData = Buffer.concat([ADMIN_DISCRIMINATORS.initialize_voting_round, roundNumberBuf, durationBuf]);

      const initInstruction = {
        programId: PROGRAM_ID,
        keys: [
          { pubkey: programAuthorityPda, isSigner: false, isWritable: false },  // program_authority
          { pubkey: publicKey, isSigner: true, isWritable: true },  // authority
          { pubkey: votingRoundPDA, isSigner: false, isWritable: true },  // voting_round
          { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },  // system_program
        ],
        data: instructionData,
      };

      const transaction = new Transaction().add(initInstruction);
      const { blockhash } = await connection.getLatestBlockhash();
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = publicKey;

      addLog('Sending transaction...');
      const signature = await sendTransaction(transaction, connection);
      addLog(`Transaction sent: ${signature.slice(0, 16)}...`);
      
      await connection.confirmTransaction(signature, 'confirmed');
      addLog('Transaction confirmed!', 'success');
      addLog(`✅ Round ${roundNum} initialized! Duration: ${durationMins} minutes`, 'success');

      // Auto-promote runners-up from previous completed rounds
      if (roundNum > 1) {
        addLog('');
        addLog('🔄 Auto-promoting runners-up from previous rounds...', 'info');
        
        // Fetch all NFTs to find runners-up
        const allNfts = await fetchAllPopperNFTs(connection, { votingEligibleOnly: false });
        addLog(`Found ${allNfts.length} total NFTs`);
        
        // Get completed rounds (rounds with winners)
        const completedRounds = allRounds?.filter(r => r.hasWinner) || [];
        addLog(`Found ${completedRounds.length} completed rounds with winners`);
        
        const runnersUpToPromote = [];
        
        // For each completed round, find 2nd and 3rd place
        for (const round of completedRounds) {
          const roundCandidates = allNfts
            .filter(nft => 
              (nft.createdInRound === round.roundNumber || nft.lastActiveRound === round.roundNumber) &&
              !nft.isWinner && // Not already a winner
              nft.isVotingEligible && // Still voting eligible
              nft.lastActiveRound < roundNum // Not already promoted to this or higher round
            )
            .sort((a, b) => {
              const aSol = a.solPerRound?.[round.roundNumber - 1] || 0;
              const bSol = b.solPerRound?.[round.roundNumber - 1] || 0;
              if (bSol !== aSol) return bSol - aSol;
              const aVotes = a.votesPerRound?.[round.roundNumber - 1] || 0;
              const bVotes = b.votesPerRound?.[round.roundNumber - 1] || 0;
              return bVotes - aVotes;
            });
          
          // Get 2nd and 3rd place (skip 1st as they're the winner)
          // But we filter out winners above, so take top 2 from remaining
          const runnersUp = roundCandidates.slice(0, 2);
          runnersUp.forEach(nft => {
            if (!runnersUpToPromote.find(r => r.nftMint === nft.nftMint)) {
              runnersUpToPromote.push({
                nftMint: nft.nftMint,
                fromRound: round.roundNumber,
              });
            }
          });
        }
        
        if (runnersUpToPromote.length === 0) {
          addLog('No runners-up found to promote', 'info');
        } else {
          addLog(`Found ${runnersUpToPromote.length} runners-up to promote`, 'info');
          
          // Promote each runner-up
          for (const runnerUp of runnersUpToPromote) {
            try {
              addLog(`Promoting ${runnerUp.nftMint.slice(0, 12)}... (from Round ${runnerUp.fromRound})`);
              
              const nftMintPubkey = new PublicKey(runnerUp.nftMint);
              const popperNftPDA = getPopperNFTPDA(nftMintPubkey);
              
              const roundBuf = Buffer.alloc(1);
              roundBuf.writeUInt8(roundNum);
              
              const promoteData = Buffer.concat([ADMIN_DISCRIMINATORS.promote_to_next_round, roundBuf]);
              
              const promoteInstruction = {
                programId: PROGRAM_ID,
                keys: [
                  { pubkey: programAuthorityPda, isSigner: false, isWritable: false },
                  { pubkey: publicKey, isSigner: true, isWritable: true },
                  { pubkey: nftMintPubkey, isSigner: false, isWritable: false },
                  { pubkey: popperNftPDA, isSigner: false, isWritable: true },
                  { pubkey: votingRoundPDA, isSigner: false, isWritable: false },
                ],
                data: promoteData,
              };
              
              const promoteTx = new Transaction().add(promoteInstruction);
              const { blockhash: promoteBlockhash } = await connection.getLatestBlockhash();
              promoteTx.recentBlockhash = promoteBlockhash;
              promoteTx.feePayer = publicKey;
              
              const promoteSig = await sendTransaction(promoteTx, connection);
              await connection.confirmTransaction(promoteSig, 'confirmed');
              addLog(`  ✅ Promoted to Round ${roundNum}`, 'success');
            } catch (promoteError) {
              console.error('Promote error:', promoteError);
              addLog(`  ❌ Failed: ${promoteError.message}`, 'error');
            }
          }
        }
      }

      addLog('');
      addLog('🎉 Round initialization complete!', 'success');

      // Reload voting round config
      setTimeout(() => {
        window.location.reload();
      }, 3000);

    } catch (error) {
      console.error('Initialize round error:', error);
      // Try to get more details from the error
      if (error.logs) {
        console.error('Transaction logs:', error.logs);
        addLog(`Logs: ${error.logs.join('\n')}`, 'error');
      }
      if (error.message) {
        addLog(`❌ Error: ${error.message}`, 'error');
      }
      // Check for simulation error details
      if (error.error) {
        console.error('Inner error:', error.error);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  // Tally votes for selected round (display only)
  const handleTallyVotes = async () => {
    const roundNum = selectedRound?.roundNumber || votingRound?.roundNumber;
    if (!roundNum) {
      alert('Please select a round first');
      return;
    }

    setIsProcessing(true);
    setAdminLogs([]);
    addLog(`Fetching vote tallies for Round ${roundNum}...`);

    try {
      const nfts = await fetchAllPopperNFTs(connection, { votingEligibleOnly: true });
      
      addLog(`Found ${nfts.length} total voting-eligible NFTs`);
      
      // Filter to only NFTs eligible for THIS specific round
      const roundEligibleNfts = nfts.filter(nft => 
        nft.createdInRound === roundNum || nft.lastActiveRound === roundNum
      );
      
      addLog(`Found ${roundEligibleNfts.length} candidates eligible for Round ${roundNum}`);
      
      // Sort by SOL in current round (primary), use votes as tiebreaker
      const sorted = roundEligibleNfts.sort((a, b) => {
        const aSol = a.solPerRound?.[roundNum - 1] || 0;
        const bSol = b.solPerRound?.[roundNum - 1] || 0;
        if (bSol !== aSol) return bSol - aSol;
        // Tiebreaker: more votes wins
        const aVotes = a.votesPerRound?.[roundNum - 1] || 0;
        const bVotes = b.votesPerRound?.[roundNum - 1] || 0;
        return bVotes - aVotes;
      });

      addLog('--- VOTE TALLY (Ranked by SOL) ---', 'success');
      sorted.forEach((nft, i) => {
        const votes = nft.votesPerRound?.[roundNum - 1] || 0;
        const sol = (nft.solPerRound?.[roundNum - 1] || 0) / LAMPORTS_PER_SOL;
        const emoji = i === 0 ? '🏆' : i === 1 ? '🥈' : i === 2 ? '🥉' : '  ';
        addLog(`${emoji} ${i + 1}. ${nft.nftMint.slice(0, 12)}... | ${sol.toFixed(4)} SOL | ${votes} votes`);
      });

    } catch (error) {
      console.error('Tally error:', error);
      addLog(`❌ Error: ${error.message}`, 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  // Refresh candidates from chain
  const handleRefreshCandidates = async () => {
    setIsLoadingCandidates(true);
    setAdminLogs([]);
    addLog('Refreshing candidates from blockchain...');

    try {
      const popperNfts = await fetchAllPopperNFTs(connection, { votingEligibleOnly: true });
      addLog(`Found ${popperNfts.length} voting-eligible NFTs`);

      const candidatesWithMetadata = await Promise.all(
        popperNfts.map(async (nft) => {
          const metadata = await fetchNFTMetadata(connection, nft.nftMint);
          // Parse stats from reserved bytes if available
          const stats = nft.reserved ? readStatsFromReserved(nft.reserved) : null;
          
          return {
            id: nft.nftMint,
            pda: nft.pda,
            name: metadata?.name || `Popper #${nft.nftMint.slice(0, 8)}`,
            description: metadata?.description || 'Community-created Popper design',
            image: metadata?.image || '/Design/silhouette.png',
            creatorWallet: nft.creatorWallet,
            totalVotes: nft.totalVotes,
            totalVoteSol: nft.totalVoteSol,
            createdAt: nft.createdAt,
            createdInRound: nft.createdInRound,
            lastActiveRound: nft.lastActiveRound,  // Include lastActiveRound for eligibility check
            stats: stats?.averages || null,  // Include stats averages for slider initialization
          };
        })
      );

      setCandidates(candidatesWithMetadata);
      addLog(`✅ Loaded ${candidatesWithMetadata.length} candidates`, 'success');

    } catch (error) {
      console.error('Refresh error:', error);
      addLog(`❌ Error: ${error.message}`, 'error');
    } finally {
      setIsLoadingCandidates(false);
    }
  };

  // Close/Skip a voting round (on-chain) - aborts without declaring winner
  const handleSkipRound = async () => {
    if (!publicKey) {
      alert('Please connect your wallet first!');
      return;
    }

    const roundNum = selectedRound?.roundNumber || votingRound?.roundNumber;
    if (!roundNum) {
      alert('Please select a round first');
      return;
    }

    if (!window.confirm(`⚠️ Are you sure you want to SKIP Round ${roundNum}?\n\nThis will close the round WITHOUT declaring a winner.\nThis action is irreversible!`)) {
      return;
    }

    setIsProcessing(true);
    setAdminLogs([]);
    addLog(`Closing Voting Round ${roundNum} (skip)...`);

    try {
      const votingRoundPDA = getVotingRoundPDA(roundNum);
      addLog(`VotingRound PDA: ${votingRoundPDA.toBase58()}`);

      // Derive program_authority PDA
      const PROGRAM_AUTHORITY_SEED = Buffer.from('program_authority');
      const [programAuthorityPda] = PublicKey.findProgramAddressSync(
        [PROGRAM_AUTHORITY_SEED],
        PROGRAM_ID
      );
      addLog(`Program Authority PDA: ${programAuthorityPda.toBase58().slice(0, 16)}...`);

      // Build close_voting_round instruction (no args)
      const instructionData = ADMIN_DISCRIMINATORS.close_voting_round;

      const closeInstruction = {
        programId: PROGRAM_ID,
        keys: [
          { pubkey: programAuthorityPda, isSigner: false, isWritable: false },  // program_authority
          { pubkey: publicKey, isSigner: true, isWritable: true },  // authority
          { pubkey: votingRoundPDA, isSigner: false, isWritable: true },  // voting_round
        ],
        data: instructionData,
      };

      const transaction = new Transaction().add(closeInstruction);
      const { blockhash } = await connection.getLatestBlockhash();
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = publicKey;

      // Simulate transaction first to get detailed errors
      addLog('Simulating transaction...');
      try {
        const simulation = await connection.simulateTransaction(transaction);
        if (simulation.value.err) {
          console.error('Simulation error:', simulation.value.err);
          console.error('Simulation logs:', simulation.value.logs);
          addLog(`Simulation failed: ${JSON.stringify(simulation.value.err)}`, 'error');
          if (simulation.value.logs) {
            simulation.value.logs.forEach(log => addLog(`  ${log}`, 'error'));
          }
          return;
        }
        addLog('Simulation passed ✓');
      } catch (simError) {
        console.error('Simulation exception:', simError);
        addLog(`Simulation exception: ${simError.message}`, 'error');
      }

      addLog('Sending close_voting_round transaction...');
      const signature = await sendTransaction(transaction, connection);
      addLog(`Transaction sent: ${signature.slice(0, 16)}...`);
      
      await connection.confirmTransaction(signature, 'confirmed');
      addLog('Transaction confirmed!', 'success');
      addLog(`✅ Round ${roundNum} closed/skipped on-chain!`, 'success');
      addLog(`Explorer: https://explorer.solana.com/tx/${signature}?cluster=devnet`, 'info');

      // Refresh rounds list
      await fetchAllRounds();

    } catch (error) {
      console.error('Skip round error:', error);
      addLog(`❌ Error: ${error.message}`, 'error');
      if (error.logs) {
        console.error('Transaction logs:', error.logs);
        error.logs.forEach(log => addLog(`  ${log}`, 'error'));
      }
    } finally {
      setIsProcessing(false);
    }
  };

  // Force-end a voting round early (admin only) - allows declaring winner before time ends
  const handleForceEndRound = async () => {
    if (!publicKey) {
      alert('Please connect your wallet first!');
      return;
    }

    const roundNum = selectedRound?.roundNumber || votingRound?.roundNumber;
    if (!roundNum) {
      alert('Please select a round first');
      return;
    }

    if (!window.confirm(`⚠️ Force-end Round ${roundNum}?\n\nThis will end voting early so you can declare a winner.\nVoters will no longer be able to vote on this round.`)) {
      return;
    }

    setIsProcessing(true);
    setAdminLogs([]);
    addLog(`Force-ending Voting Round ${roundNum}...`);

    try {
      const votingRoundPDA = getVotingRoundPDA(roundNum);
      addLog(`VotingRound PDA: ${votingRoundPDA.toBase58().slice(0, 16)}...`);

      // Derive program_authority PDA
      const PROGRAM_AUTHORITY_SEED = Buffer.from('program_authority');
      const [programAuthorityPda] = PublicKey.findProgramAddressSync(
        [PROGRAM_AUTHORITY_SEED],
        PROGRAM_ID
      );
      addLog(`Program Authority PDA: ${programAuthorityPda.toBase58().slice(0, 16)}...`);

      // Build force_end_round instruction (no args)
      const instructionData = ADMIN_DISCRIMINATORS.force_end_round;

      const forceEndInstruction = {
        programId: PROGRAM_ID,
        keys: [
          { pubkey: programAuthorityPda, isSigner: false, isWritable: false },  // program_authority
          { pubkey: publicKey, isSigner: true, isWritable: true },  // authority
          { pubkey: votingRoundPDA, isSigner: false, isWritable: true },  // voting_round
        ],
        data: instructionData,
      };

      const transaction = new Transaction().add(forceEndInstruction);
      const { blockhash } = await connection.getLatestBlockhash();
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = publicKey;

      // Simulate transaction first
      addLog('Simulating transaction...');
      try {
        const simulation = await connection.simulateTransaction(transaction);
        if (simulation.value.err) {
          console.error('Simulation error:', simulation.value.err);
          console.error('Simulation logs:', simulation.value.logs);
          addLog(`Simulation failed: ${JSON.stringify(simulation.value.err)}`, 'error');
          if (simulation.value.logs) {
            simulation.value.logs.forEach(log => addLog(`  ${log}`, 'error'));
          }
          return;
        }
        addLog('Simulation passed ✓');
      } catch (simError) {
        console.error('Simulation exception:', simError);
        addLog(`Simulation exception: ${simError.message}`, 'error');
      }

      addLog('Sending force_end_round transaction...');
      const signature = await sendTransaction(transaction, connection);
      addLog(`Transaction sent: ${signature.slice(0, 16)}...`);
      
      await connection.confirmTransaction(signature, 'confirmed');
      addLog('Transaction confirmed!', 'success');
      addLog(`✅ Round ${roundNum} force-ended! You can now declare a winner.`, 'success');
      addLog(`Explorer: https://explorer.solana.com/tx/${signature}?cluster=devnet`, 'info');

      // Refresh rounds list
      await fetchAllRounds();

    } catch (error) {
      console.error('Force end round error:', error);
      addLog(`❌ Error: ${error.message}`, 'error');
      if (error.logs) {
        console.error('Transaction logs:', error.logs);
        error.logs.forEach(log => addLog(`  ${log}`, 'error'));
      }
    } finally {
      setIsProcessing(false);
    }
  };

  // Declare winner and end round (on-chain)
  const handleDeclareWinner = async () => {
    if (!publicKey) {
      alert('Please connect your wallet first!');
      return;
    }

    const roundNum = selectedRound?.roundNumber || votingRound?.roundNumber;
    if (!roundNum) {
      alert('Please select a round first');
      return;
    }

    if (candidates.length === 0) {
      alert('No candidates to declare as winner');
      return;
    }

    setIsProcessing(true);
    setAdminLogs([]);
    addLog(`Finding winner for Round ${roundNum}...`);

    try {
      // Fetch fresh NFT data and find the winner
      const nfts = await fetchAllPopperNFTs(connection, { votingEligibleOnly: true });
      
      if (nfts.length === 0) {
        addLog('No voting-eligible NFTs found!', 'error');
        return;
      }

      // Filter to only NFTs eligible for THIS specific round
      const roundEligibleNfts = nfts.filter(nft => 
        nft.createdInRound === roundNum || nft.lastActiveRound === roundNum
      );
      
      if (roundEligibleNfts.length === 0) {
        addLog(`No NFTs eligible for Round ${roundNum}!`, 'error');
        return;
      }
      
      addLog(`Found ${roundEligibleNfts.length} candidates for Round ${roundNum}`);

      // Sort by SOL in current round (primary), use votes as tiebreaker
      const sorted = roundEligibleNfts.sort((a, b) => {
        const aSol = a.solPerRound?.[roundNum - 1] || 0;
        const bSol = b.solPerRound?.[roundNum - 1] || 0;
        if (bSol !== aSol) return bSol - aSol;
        // Tiebreaker: more votes wins
        const aVotes = a.votesPerRound?.[roundNum - 1] || 0;
        const bVotes = b.votesPerRound?.[roundNum - 1] || 0;
        return bVotes - aVotes;
      });

      const winner = sorted[0];
      const winnerVotes = winner.votesPerRound?.[roundNum - 1] || 0;
      const winnerSol = (winner.solPerRound?.[roundNum - 1] || 0) / LAMPORTS_PER_SOL;
      addLog(`Winner: ${winner.nftMint.slice(0, 16)}... with ${winnerVotes} votes (${winnerSol.toFixed(4)} SOL)`);

      if (!window.confirm(`🏆 Declare winner?\n\nNFT: ${winner.nftMint.slice(0, 20)}...\nVotes: ${winnerVotes}\nSOL: ${winnerSol.toFixed(4)}\n\nThis will mark this NFT as winner ON-CHAIN!`)) {
        setIsProcessing(false);
        return;
      }

      const votingRoundPDA = getVotingRoundPDA(roundNum);
      const nftMintPubkey = new PublicKey(winner.nftMint);
      const popperNftPDA = getPopperNFTPDA(nftMintPubkey);

      addLog(`VotingRound PDA: ${votingRoundPDA.toBase58().slice(0, 16)}...`);
      addLog(`PopperNFT PDA: ${popperNftPDA.toBase58().slice(0, 16)}...`);

      // Derive program_authority PDA
      const PROGRAM_AUTHORITY_SEED = Buffer.from('program_authority');
      const [programAuthorityPda] = PublicKey.findProgramAddressSync(
        [PROGRAM_AUTHORITY_SEED],
        PROGRAM_ID
      );
      addLog(`Program Authority PDA: ${programAuthorityPda.toBase58().slice(0, 16)}...`);

      // Build declare_winner instruction with product_id arg (u32)
      const productIdBuf = Buffer.alloc(4);
      productIdBuf.writeUInt32LE(1); // product_id = 1 (placeholder)
      
      const instructionData = Buffer.concat([ADMIN_DISCRIMINATORS.declare_winner, productIdBuf]);

      const declareInstruction = {
        programId: PROGRAM_ID,
        keys: [
          { pubkey: programAuthorityPda, isSigner: false, isWritable: false },  // program_authority
          { pubkey: publicKey, isSigner: true, isWritable: true },  // authority
          { pubkey: nftMintPubkey, isSigner: false, isWritable: false },  // nft_mint
          { pubkey: popperNftPDA, isSigner: false, isWritable: true },  // popper_nft
          { pubkey: votingRoundPDA, isSigner: false, isWritable: true },  // voting_round_account (must be writable!)
          { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },  // system_program
        ],
        data: instructionData,
      };

      const transaction = new Transaction().add(declareInstruction);
      const { blockhash } = await connection.getLatestBlockhash();
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = publicKey;

      // Simulate transaction first to get detailed errors
      addLog('Simulating transaction...');
      try {
        const simulation = await connection.simulateTransaction(transaction);
        if (simulation.value.err) {
          console.error('Simulation error:', simulation.value.err);
          console.error('Simulation logs:', simulation.value.logs);
          addLog(`Simulation failed: ${JSON.stringify(simulation.value.err)}`, 'error');
          if (simulation.value.logs) {
            simulation.value.logs.forEach(log => addLog(`  ${log}`, 'error'));
          }
          return;
        }
        addLog('Simulation passed ✓');
      } catch (simError) {
        console.error('Simulation exception:', simError);
        addLog(`Simulation exception: ${simError.message}`, 'error');
      }

      addLog('Sending declare_winner transaction...');
      const signature = await sendTransaction(transaction, connection);
      addLog(`Transaction sent: ${signature.slice(0, 16)}...`);
      
      await connection.confirmTransaction(signature, 'confirmed');
      addLog('Transaction confirmed!', 'success');
      addLog(`🏆 Winner declared on-chain!`, 'success');
      addLog(`Explorer: https://explorer.solana.com/tx/${signature}?cluster=devnet`, 'info');

      setTimeout(() => {
        window.location.reload();
      }, 3000);

    } catch (error) {
      console.error('Declare winner error:', error);
      addLog(`❌ Error: ${error.message}`, 'error');
      // Try to extract more error details
      if (error.logs) {
        console.error('Transaction logs:', error.logs);
        error.logs.forEach(log => addLog(`  ${log}`, 'error'));
      }
      if (error.error) {
        console.error('Inner error:', error.error);
        addLog(`Inner error: ${JSON.stringify(error.error)}`, 'error');
      }
    } finally {
      setIsProcessing(false);
    }
  };

  // Promote NFT to a new round (allows runner-ups to compete again)
  const handlePromoteNFT = async () => {
    if (!publicKey) {
      alert('Please connect your wallet first!');
      return;
    }

    if (!promoteNftMint || promoteNftMint.trim().length < 32) {
      alert('Please enter a valid NFT mint address');
      return;
    }

    const targetRound = parseInt(promoteToRound);
    if (isNaN(targetRound) || targetRound < 1 || targetRound > 255) {
      alert('Please enter a valid round number (1-255)');
      return;
    }

    setIsProcessing(true);
    setAdminLogs([]);
    addLog(`Promoting NFT to Round ${targetRound}...`);
    addLog(`NFT Mint: ${promoteNftMint.slice(0, 20)}...`);

    try {
      const nftMintPubkey = new PublicKey(promoteNftMint.trim());
      const popperNftPDA = getPopperNFTPDA(nftMintPubkey);
      const votingRoundPDA = getVotingRoundPDA(targetRound);

      addLog(`PopperNFT PDA: ${popperNftPDA.toBase58().slice(0, 16)}...`);
      addLog(`Target VotingRound PDA: ${votingRoundPDA.toBase58().slice(0, 16)}...`);

      // Derive program_authority PDA
      const PROGRAM_AUTHORITY_SEED = Buffer.from('program_authority');
      const [programAuthorityPda] = PublicKey.findProgramAddressSync(
        [PROGRAM_AUTHORITY_SEED],
        PROGRAM_ID
      );
      addLog(`Program Authority PDA: ${programAuthorityPda.toBase58().slice(0, 16)}...`);

      // Build promote_to_next_round instruction
      // Args: new_round (u8)
      const roundBuf = Buffer.alloc(1);
      roundBuf.writeUInt8(targetRound);
      
      const instructionData = Buffer.concat([ADMIN_DISCRIMINATORS.promote_to_next_round, roundBuf]);

      const promoteInstruction = {
        programId: PROGRAM_ID,
        keys: [
          { pubkey: programAuthorityPda, isSigner: false, isWritable: false },  // program_authority
          { pubkey: publicKey, isSigner: true, isWritable: true },  // authority
          { pubkey: nftMintPubkey, isSigner: false, isWritable: false },  // nft_mint
          { pubkey: popperNftPDA, isSigner: false, isWritable: true },  // popper_nft
          { pubkey: votingRoundPDA, isSigner: false, isWritable: false },  // new_voting_round
        ],
        data: instructionData,
      };

      const transaction = new Transaction().add(promoteInstruction);
      const { blockhash } = await connection.getLatestBlockhash();
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = publicKey;

      // Simulate first
      addLog('Simulating transaction...');
      try {
        const simulation = await connection.simulateTransaction(transaction);
        if (simulation.value.err) {
          console.error('Simulation error:', simulation.value.err);
          console.error('Simulation logs:', simulation.value.logs);
          addLog(`Simulation failed: ${JSON.stringify(simulation.value.err)}`, 'error');
          if (simulation.value.logs) {
            simulation.value.logs.forEach(log => addLog(`  ${log}`, 'error'));
          }
          return;
        }
        addLog('Simulation passed ✓');
      } catch (simError) {
        console.error('Simulation exception:', simError);
        addLog(`Simulation exception: ${simError.message}`, 'error');
      }

      addLog('Sending promote_to_next_round transaction...');
      const signature = await sendTransaction(transaction, connection);
      addLog(`Transaction sent: ${signature.slice(0, 16)}...`);
      
      await connection.confirmTransaction(signature, 'confirmed');
      addLog('Transaction confirmed!', 'success');
      addLog(`✅ NFT promoted to Round ${targetRound}!`, 'success');
      addLog(`Explorer: https://explorer.solana.com/tx/${signature}?cluster=devnet`, 'info');

      // Clear inputs
      setPromoteNftMint('');
      setPromoteToRound('');

      // Refresh candidates
      await handleRefreshCandidates();

    } catch (error) {
      console.error('Promote NFT error:', error);
      addLog(`❌ Error: ${error.message}`, 'error');
      if (error.logs) {
        console.error('Transaction logs:', error.logs);
        error.logs.forEach(log => addLog(`  ${log}`, 'error'));
      }
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="vote-page">
      {/* Admin Panel Toggle */}
      {isAdmin && (
        <div className="admin-panel-banner">
          <span>🛠️ ADMIN MODE</span>
          <button onClick={() => setShowAdminPanel(!showAdminPanel)}>
            {showAdminPanel ? '✕ Close Panel' : '📊 Open Admin Panel'}
          </button>
        </div>
      )}

      {/* Admin Panel */}
      {isAdmin && showAdminPanel && (
        <div className="admin-panel">
          <button 
            className="admin-panel-close" 
            onClick={() => setShowAdminPanel(false)}
            title="Close admin panel"
          >
            ✕
          </button>
          <h3>🗳️ Voting Round Controls</h3>
          
          {/* Round Info Table - Shows all on-chain rounds */}
          <div className="admin-section">
            <h4>
              📋 Round Info
              <button 
                onClick={fetchAllRounds} 
                disabled={isLoadingRounds}
                className="refresh-btn"
                title="Refresh rounds from chain"
              >
                {isLoadingRounds ? '⏳' : '🔄'}
              </button>
            </h4>
            {isLoadingRounds ? (
              <div className="loading-rounds">Loading rounds from chain...</div>
            ) : allRounds.length === 0 ? (
              <div className="no-rounds">No voting rounds found on-chain</div>
            ) : (
              <div className="rounds-table-container">
                <table className="rounds-table">
                  <thead>
                    <tr>
                      <th>Round</th>
                      <th>Candidates</th>
                      <th>Status</th>
                      <th>End Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allRounds.map((round) => {
                      // Count candidates eligible for this round (created or promoted)
                      const roundCandidateCount = candidates.filter(c => 
                        c.createdInRound === round.roundNumber || c.lastActiveRound === round.roundNumber
                      ).length;
                      
                      return (
                      <tr 
                        key={round.roundNumber}
                        className={`round-row ${selectedRound?.roundNumber === round.roundNumber ? 'selected' : ''} ${round.isActive ? 'active' : round.isEnded ? 'ended' : ''}`}
                        onClick={() => setSelectedRound(round)}
                      >
                        <td className="round-number">#{round.roundNumber}</td>
                        <td className="round-candidates">{roundCandidateCount}</td>
                        <td className="round-status">
                          {round.hasWinner ? (
                            <span className="status-badge winner">🏆 Winner</span>
                          ) : round.isActive ? (
                            <span className="status-badge active">🟢 Active</span>
                          ) : round.isEnded ? (
                            <span className="status-badge ended">🔴 Ended</span>
                          ) : (
                            <span className="status-badge pending">⏳ Pending</span>
                          )}
                        </td>
                        <td className="round-end-time">
                          {new Date(round.endTimestamp * 1000).toLocaleString()}
                        </td>
                      </tr>
                    );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Selected Round Details Panel */}
          {selectedRound && (
            <div className="admin-section selected-round-details">
              <h4>📍 Round {selectedRound.roundNumber} Details</h4>
              <div className="round-details-grid">
                <div className="detail-item">
                  <span className="detail-label">PDA:</span>
                  <a 
                    href={`https://explorer.solana.com/address/${selectedRound.pda}?cluster=devnet`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="explorer-link small"
                  >
                    {selectedRound.pda.slice(0, 20)}...
                  </a>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Start:</span>
                  <span>{new Date(selectedRound.startTimestamp * 1000).toLocaleString()}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">End:</span>
                  <span>{new Date(selectedRound.endTimestamp * 1000).toLocaleString()}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Total Votes:</span>
                  <span>{selectedRound.totalVotesCast || 0}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">NFTs Created (this round):</span>
                  <span>{selectedRound.totalNftsCreated || 0}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Total Eligible Candidates:</span>
                  <span>{candidates.filter(c => c.createdInRound === selectedRound.roundNumber || c.lastActiveRound === selectedRound.roundNumber).length}</span>
                </div>
                {selectedRound.hasWinner && (
                  <div className="detail-item">
                    <span className="detail-label">Winner:</span>
                    <a 
                      href={`https://explorer.solana.com/address/${selectedRound.winnerMint}?cluster=devnet`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="explorer-link small"
                    >
                      {selectedRound.winnerMint.slice(0, 16)}...
                    </a>
                  </div>
                )}
              </div>

              {/* Actions for selected round */}
              <div className="selected-round-actions">
                <h5>⚡ Actions for Round {selectedRound.roundNumber}</h5>
                {selectedRound.isActive && !selectedRound.hasWinner ? (
                  <div className="admin-actions">
                    <button 
                      onClick={handleTallyVotes} 
                      disabled={isProcessing}
                      className="admin-btn"
                    >
                      📊 Tally Votes
                    </button>
                    <button 
                      onClick={handleForceEndRound}
                      disabled={isProcessing || !publicKey}
                      className="admin-btn warning"
                    >
                      ⏹️ Force End Round
                    </button>
                    <button 
                      onClick={handleDeclareWinner}
                      disabled={isProcessing || !publicKey || candidates.length === 0}
                      className="admin-btn primary"
                    >
                      🏆 Declare Winner
                    </button>
                    <button 
                      onClick={handleSkipRound}
                      disabled={isProcessing || !publicKey}
                      className="admin-btn danger"
                    >
                      🗑️ Close Round
                    </button>
                  </div>
                ) : selectedRound.isEnded && !selectedRound.hasWinner ? (
                  <div className="admin-actions">
                    <button 
                      onClick={handleTallyVotes} 
                      disabled={isProcessing}
                      className="admin-btn"
                    >
                      📊 View Final Tally
                    </button>
                    <button 
                      onClick={handleDeclareWinner}
                      disabled={isProcessing || !publicKey || candidates.length === 0}
                      className="admin-btn primary"
                    >
                      🏆 Declare Winner
                    </button>
                    <button 
                      onClick={handleSkipRound}
                      disabled={isProcessing || !publicKey}
                      className="admin-btn danger"
                    >
                      🗑️ Close Round
                    </button>
                  </div>
                ) : selectedRound.hasWinner ? (
                  <>
                    <div className="round-complete-notice">
                      ✅ This round is complete. Winner has been declared.
                    </div>
                    
                    {/* Leaderboard: Top 3 Candidates */}
                    {(() => {
                      // Filter candidates for this round and sort by SOL (primary), votes as tiebreaker
                      const roundCandidates = candidates
                        .filter(c => 
                          c.createdInRound === selectedRound.roundNumber || 
                          c.lastActiveRound === selectedRound.roundNumber
                        )
                        .sort((a, b) => {
                          const solDiff = Number(b.totalVoteSol || 0) - Number(a.totalVoteSol || 0);
                          if (solDiff !== 0) return solDiff;
                          return Number(b.totalVotes) - Number(a.totalVotes);
                        })
                        .slice(0, 3);
                      
                      if (roundCandidates.length === 0) {
                        return (
                          <div className="round-leaderboard">
                            <h5>🏆 Final Results</h5>
                            <p className="no-candidates-notice">No candidates found for this round.</p>
                          </div>
                        );
                      }
                      
                      return (
                        <div className="round-leaderboard">
                          <h5>🏆 Final Results</h5>
                          {roundCandidates.map((candidate, index) => {
                            const rank = index + 1;
                            const isWinner = rank === 1;
                            const medal = rank === 1 ? '🥇' : rank === 2 ? '🥈' : '🥉';
                            const voteSol = (Number(candidate.totalVoteSol) / 1e9).toFixed(4);
                            
                            return (
                              <div key={candidate.id} className={`leaderboard-entry ${isWinner ? 'winner' : 'runner-up'}`}>
                                <div className="leaderboard-rank">
                                  <span className="medal">{medal}</span>
                                  <span className="rank-number">#{rank}</span>
                                </div>
                                <div className="leaderboard-info">
                                  <div className="leaderboard-name">{candidate.name}</div>
                                  <div className="leaderboard-stats">
                                    <span className="stat">💰 {voteSol} SOL</span>
                                    <span className="stat">🗳️ {Number(candidate.totalVotes)} votes</span>
                                  </div>
                                  <div className="leaderboard-mint">
                                    <span className="mint-label">Mint:</span>
                                    <a 
                                      href={`https://explorer.solana.com/address/${candidate.id}?cluster=devnet`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="mint-link"
                                    >
                                      {candidate.id}
                                    </a>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </>
                ) : (
                  <div className="round-pending-notice">
                    ⏳ This round hasn't started yet.
                  </div>
                )}
                {!publicKey && <p className="admin-warning">⚠️ Connect wallet to use on-chain actions</p>}
              </div>
            </div>
          )}

          {/* Quick Actions (Global) */}
          <div className="admin-section">
            <h4>🔧 Quick Actions</h4>
            <div className="admin-actions">
              <button 
                onClick={handleRefreshCandidates} 
                disabled={isProcessing || isLoadingCandidates}
                className="admin-btn"
              >
                🔄 Refresh Candidates
              </button>
            </div>
          </div>

          {/* Promote NFT to New Round */}
          <div className="admin-section">
            <h4>🚀 Promote NFT to Round</h4>
            <p className="admin-hint">
              Allow runner-up NFTs to compete in a new round. The NFT's last_active_round will be updated.
            </p>
            <div className="admin-form">
              <div className="admin-form-row">
                <label>NFT Mint Address:</label>
                <input
                  type="text"
                  value={promoteNftMint}
                  onChange={(e) => setPromoteNftMint(e.target.value)}
                  placeholder="e.g., ABC123..."
                  style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                />
              </div>
              <div className="admin-form-row">
                <label>Promote to Round:</label>
                <input
                  type="number"
                  value={promoteToRound}
                  onChange={(e) => setPromoteToRound(e.target.value)}
                  placeholder={`e.g., ${votingRound?.roundNumber || 1}`}
                  min="1"
                  max="255"
                />
              </div>
              <button 
                onClick={handlePromoteNFT}
                disabled={isProcessing || !publicKey || !promoteNftMint || !promoteToRound}
                className="admin-btn primary"
              >
                🎖️ Promote NFT
              </button>
              {!publicKey && <p className="admin-warning">⚠️ Connect wallet first</p>}
            </div>
          </div>

          {/* Admin Logs */}
          {adminLogs.length > 0 && (
            <div className="admin-section">
              <h4>📝 Logs</h4>
              <div className="admin-logs">
                {adminLogs.map((log, i) => (
                  <div key={i} className={`log-entry log-${log.type}`}>
                    <span className="log-time">[{log.timestamp}]</span> {log.message}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Initialize New Round - at bottom */}
          <div className="admin-section">
            <h4>🚀 Initialize New Round (On-Chain)</h4>
            <div className="admin-form">
              <div className="admin-form-row">
                <label>Round Number:</label>
                <input
                  type="number"
                  value={newRoundNumber}
                  onChange={(e) => setNewRoundNumber(e.target.value)}
                  placeholder={`e.g., ${(allRounds[0]?.roundNumber || 0) + 1}`}
                  min="1"
                  max="255"
                />
              </div>
              <div className="admin-form-row">
                <label>Duration (minutes):</label>
                <input
                  type="number"
                  value={roundDuration}
                  onChange={(e) => setRoundDuration(e.target.value)}
                  placeholder="60"
                  min="1"
                />
              </div>
              <button 
                onClick={handleInitializeRound}
                disabled={isProcessing || !publicKey}
                className="admin-btn primary"
              >
                🚀 Initialize Round
              </button>
              {!publicKey && <p className="admin-warning">⚠️ Connect wallet first</p>}
            </div>
          </div>

          {/* Command Reference - at very bottom */}
          <div className="admin-section">
            <h4>📖 Command Reference</h4>
            <div className="admin-commands">
              <code>node scripts/end-round.js --round N</code>
              <p>End round, declare winner, archive losers</p>
              <code>node scripts/skip-round.js --round N</code>
              <p>Close round without declaring winner</p>
              <code>node scripts/tally-votes.js --round N</code>
              <p>Display vote tallies</p>
            </div>
          </div>
        </div>
      )}

      {/* Countdown Timer */}
      <div className="countdown-container">
        {isLoadingRound ? (
          <>
            <h3>Loading Voting Round...</h3>
            <div className="countdown-timer">
              <div className="time-segment">
                <span className="time-value">-</span>
                <span className="time-label">Days</span>
              </div>
              <div className="time-segment">
                <span className="time-value">-</span>
                <span className="time-label">Hours</span>
              </div>
              <div className="time-segment">
                <span className="time-value">-</span>
                <span className="time-label">Minutes</span>
              </div>
              <div className="time-segment">
                <span className="time-value">-</span>
                <span className="time-label">Seconds</span>
              </div>
            </div>
            <p className="countdown-date">Fetching from blockchain...</p>
          </>
        ) : noActiveRound ? (
          <>
            <h3>No Active Voting Round</h3>
            <div className="countdown-timer">
              <div className="time-segment">
                <span className="time-value">-</span>
                <span className="time-label">Days</span>
              </div>
              <div className="time-segment">
                <span className="time-value">-</span>
                <span className="time-label">Hours</span>
              </div>
              <div className="time-segment">
                <span className="time-value">-</span>
                <span className="time-label">Minutes</span>
              </div>
              <div className="time-segment">
                <span className="time-value">-</span>
                <span className="time-label">Seconds</span>
              </div>
            </div>
            <p className="countdown-date">Waiting for admin to initialize a new round</p>
          </>
        ) : (
          <>
            <h3>Voting Round {votingRound?.roundNumber || '?'} {votingEnded ? 'Ended' : 'Ends In'}:</h3>
            <div className="countdown-timer">
              <div className="time-segment">
                <span className="time-value">{countdown.days}</span>
                <span className="time-label">Days</span>
              </div>
              <div className="time-segment">
                <span className="time-value">{countdown.hours}</span>
                <span className="time-label">Hours</span>
              </div>
              <div className="time-segment">
                <span className="time-value">{countdown.minutes}</span>
                <span className="time-label">Minutes</span>
              </div>
              <div className="time-segment">
                <span className="time-value">{countdown.seconds}</span>
                <span className="time-label">Seconds</span>
              </div>
            </div>
            <p className="countdown-date">
              {votingRound?.endTimestamp 
                ? new Date(votingRound.endTimestamp * 1000).toLocaleString()
                : 'N/A'}
            </p>
          </>
        )}
      </div>

      <h2>Vote for Next Design!</h2>
      <p className="vote-description">
        Spend SOL to vote on which Popper design becomes our next plushie!
      </p>

      {votingEnded && !noActiveRound && (
        <div className="voting-ended-notice">
          <p>🗳️ Voting Round {votingRound?.roundNumber} has ended! Results are being tallied.</p>
        </div>
      )}
      
      {noActiveRound && (
        <div className="voting-ended-notice" style={{background: 'rgba(100, 100, 100, 0.3)', borderColor: '#888'}}>
          <p>⏳ No active voting round. Check back soon!</p>
        </div>
      )}

      <div className="vote-controls">
        <label htmlFor="voteAmount">Vote Amount (SOL):</label>
        <div className="amount-selector">
          <button 
            className="amount-btn"
            onClick={() => setVoteAmount(0.1)}
            disabled={isVoting || votingEnded}
          >
            0.1 SOL
          </button>
          <button 
            className="amount-btn"
            onClick={() => setVoteAmount(0.5)}
            disabled={isVoting || votingEnded}
          >
            0.5 SOL
          </button>
          <button 
            className="amount-btn"
            onClick={() => setVoteAmount(1)}
            disabled={isVoting || votingEnded}
          >
            1 SOL
          </button>
          <input
            type="number"
            id="voteAmount"
            min="0.01"
            step="0.01"
            value={voteAmount}
            onChange={(e) => setVoteAmount(parseFloat(e.target.value) || 0)}
            className="custom-amount"
            disabled={isVoting || votingEnded}
          />
        </div>
      </div>

      {/* Show leaderboard when no active round, otherwise show voting grid */}
      {noActiveRound && completedRoundsData.length > 0 ? (
        <div className="public-leaderboard">
          <div className="leaderboard-header">
            <h3>🏆 Previous Voting Rounds</h3>
            <p className="leaderboard-subtitle">
              Click on a round to view results
            </p>
          </div>
          
          <div className="rounds-accordion">
            {completedRoundsData.map(({ round, candidates: roundCandidates }) => {
              const isExpanded = expandedRound === round.roundNumber;
              const winnerName = round.hasWinner && roundCandidates.length > 0 ? roundCandidates[0]?.name : null;
              
              return (
                <div key={round.roundNumber} className={`accordion-item ${isExpanded ? 'expanded' : ''}`}>
                  <button 
                    className="accordion-header"
                    onClick={() => setExpandedRound(isExpanded ? null : round.roundNumber)}
                  >
                    <div className="accordion-title">
                      <span className="round-number">Round {round.roundNumber}</span>
                      {round.hasWinner && <span className="winner-badge">🏆 Winner Declared</span>}
                      {winnerName && <span className="winner-name">{winnerName}</span>}
                    </div>
                    <div className="accordion-meta">
                      <span className="round-date">
                        {new Date(round.endTimestamp * 1000).toLocaleDateString()}
                      </span>
                      <span className="accordion-arrow">{isExpanded ? '▲' : '▼'}</span>
                    </div>
                  </button>
                  
                  {isExpanded && (
                    <div className="accordion-content">
                      {roundCandidates.length === 0 ? (
                        <div className="no-candidates-mini">
                          <span>No Poppers participated in this round.</span>
                        </div>
                      ) : (
                        <div className="leaderboard-grid">
                          {roundCandidates.map((candidate, index) => {
                            const rank = index + 1;
                            const medal = rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : `#${rank}`;
                            const voteSol = (Number(candidate.totalVoteSol) / 1e9).toFixed(4);
                            
                            return (
                              <div 
                                key={candidate.id} 
                                className={`leaderboard-card ${rank <= 3 ? 'top-three' : ''} ${rank === 1 ? 'winner' : ''}`}
                                onClick={() => {
                                  setSelectedPopper(candidate);
                                  setShowStatsModal(true);
                                }}
                              >
                                <div className="leaderboard-card-rank">
                                  <span className="rank-badge">{medal}</span>
                                </div>
                                <div className={`product-frame ${candidate.frame || 'bronze'}-bg`}>
                                  <img 
                                    src={candidate.image} 
                                    alt={candidate.name}
                                    className="product-image"
                                  />
                                </div>
                                <h4>{candidate.name}</h4>
                                <div className="leaderboard-card-stats">
                                  <div className="stat-item">
                                    <span className="stat-label">Total SOL</span>
                                    <span className="stat-value">{voteSol}</span>
                                  </div>
                                  <div className="stat-item">
                                    <span className="stat-label">Votes</span>
                                    <span className="stat-value">{Number(candidate.totalVotes)}</span>
                                  </div>
                                </div>
                                <button className="view-stats-btn">
                                  📊 View Stats
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="vote-grid">
          {isLoadingCandidates ? (
            <div className="no-candidates">
              <div className="empty-state">
                <span className="empty-icon">⏳</span>
                <h3>Loading Candidates...</h3>
                <p>Fetching designs from the blockchain...</p>
              </div>
            </div>
          ) : voteOptions.length === 0 ? (
            <div className="no-candidates">
              <div className="empty-state">
                <span className="empty-icon">🎨</span>
                <h3>No Candidates Yet</h3>
                <p>Be the first to create a Popper design!</p>
                <p className="empty-hint">Head to the <a href="/create">Create page</a> to design and mint your NFT.</p>
              </div>
            </div>
          ) : (
            voteOptions.map((option) => (
            <div key={option.id} className="vote-card">
              <div className={`product-frame ${option.frame}-bg`}>
                <img 
                  src={option.image} 
                  alt={option.name}
                  className="product-image vote-image"
                />
              </div>
              <h4>{option.name}</h4>
              
              {/* Only show vote stats after voting has ended */}
              {votingEnded && (
                <div className="vote-stats">
                  <p className="vote-count">{votes[option.id] || 0} votes</p>
                  <p className="sol-amount">{(totalSOL[option.id] || 0).toFixed(2)} SOL</p>
                </div>
              )}
              
              <button
                className="vote-btn"
                onClick={() => handleVote(option.id)}
                disabled={isVoting || !publicKey || votingEnded}
              >
                {isVoting ? 'Processing...' : votingEnded ? 'Voting Ended' : `Vote with ${voteAmount} SOL`}
              </button>
            </div>
          ))
        )}
        </div>
      )}

      {/* Popper Stats Modal */}
      {showStatsModal && selectedPopper && (
        <div className="modal-overlay" onClick={() => setShowStatsModal(false)}>
          <div className="stats-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowStatsModal(false)}>
              ✕
            </button>
            
            <div className="stats-modal-content">
              <div className="stats-modal-image">
                <div className={`product-frame ${selectedPopper.frame || 'bronze'}-bg`}>
                  <img 
                    src={selectedPopper.image} 
                    alt={selectedPopper.name}
                    className="product-image"
                  />
                </div>
              </div>
              
              <div className="stats-modal-info">
                <h2>{selectedPopper.name}</h2>
                <p className="stats-description">{selectedPopper.description}</p>
                
                <div className="stats-grid">
                  <div className="stat-box">
                    <span className="stat-icon">💰</span>
                    <span className="stat-label">Total SOL</span>
                    <span className="stat-value">{(Number(selectedPopper.totalVoteSol) / 1e9).toFixed(4)}</span>
                  </div>
                  
                  <div className="stat-box">
                    <span className="stat-icon">🗳️</span>
                    <span className="stat-label">Total Votes</span>
                    <span className="stat-value">{Number(selectedPopper.totalVotes)}</span>
                  </div>
                  
                  <div className="stat-box">
                    <span className="stat-icon">🎨</span>
                    <span className="stat-label">Created In</span>
                    <span className="stat-value">Round {selectedPopper.createdInRound}</span>
                  </div>
                  
                  <div className="stat-box">
                    <span className="stat-icon">🔄</span>
                    <span className="stat-label">Last Active</span>
                    <span className="stat-value">Round {selectedPopper.lastActiveRound}</span>
                  </div>
                </div>
                
                <div className="stats-details">
                  <div className="detail-row">
                    <span className="detail-label">Mint Address:</span>
                    <a 
                      href={`https://explorer.solana.com/address/${selectedPopper.id}?cluster=devnet`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="detail-link"
                    >
                      {selectedPopper.id}
                    </a>
                  </div>
                  
                  <div className="detail-row">
                    <span className="detail-label">Creator:</span>
                    <a 
                      href={`https://explorer.solana.com/address/${selectedPopper.creatorWallet}?cluster=devnet`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="detail-link"
                    >
                      {selectedPopper.creatorWallet.slice(0, 8)}...{selectedPopper.creatorWallet.slice(-8)}
                    </a>
                  </div>
                  
                  <div className="detail-row">
                    <span className="detail-label">Created:</span>
                    <span className="detail-value">
                      {new Date(selectedPopper.createdAt * 1000).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rating Modal - shown when voting */}
      {showRatingModal && ratingCandidate && (
        <div className="modal-overlay rating-modal-overlay" onClick={() => setShowRatingModal(false)}>
          <div className="rating-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowRatingModal(false)}>
              ✕
            </button>
            
            <div className="rating-modal-header">
              <div className={`rating-modal-image ${ratingCandidate.frame || 'bronze'}-bg`}>
                <img 
                  src={ratingCandidate.image} 
                  alt={ratingCandidate.name}
                />
              </div>
              <div className="rating-modal-title">
                <h2>Vote for {ratingCandidate.name}</h2>
                <p className="rating-modal-subtitle">Rate this Popper before voting!</p>
              </div>
            </div>
            
            <div className="rating-sliders">
              {/* Cuteness */}
              <div className="rating-slider-row">
                <div className="rating-slider-label">
                  <span className="rating-slider-emoji">🥰</span>
                  <span>Cuteness</span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={ratings.cuteness}
                  onChange={(e) => setRatings({...ratings, cuteness: parseInt(e.target.value)})}
                  className="rating-slider-input"
                />
                <span className="rating-slider-value">{ratings.cuteness}</span>
              </div>
              
              {/* Creativity */}
              <div className="rating-slider-row">
                <div className="rating-slider-label">
                  <span className="rating-slider-emoji">🎨</span>
                  <span>Creativity</span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={ratings.creativity}
                  onChange={(e) => setRatings({...ratings, creativity: parseInt(e.target.value)})}
                  className="rating-slider-input"
                />
                <span className="rating-slider-value">{ratings.creativity}</span>
              </div>
              
              {/* Pop Power */}
              <div className="rating-slider-row">
                <div className="rating-slider-label">
                  <span className="rating-slider-emoji">💥</span>
                  <span>Pop Power</span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={ratings.popPower}
                  onChange={(e) => setRatings({...ratings, popPower: parseInt(e.target.value)})}
                  className="rating-slider-input"
                />
                <span className="rating-slider-value">{ratings.popPower}</span>
              </div>
              
              {/* Originality */}
              <div className="rating-slider-row">
                <div className="rating-slider-label">
                  <span className="rating-slider-emoji">✨</span>
                  <span>Originality</span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={ratings.originality}
                  onChange={(e) => setRatings({...ratings, originality: parseInt(e.target.value)})}
                  className="rating-slider-input"
                />
                <span className="rating-slider-value">{ratings.originality}</span>
              </div>
              
              {/* Potential */}
              <div className="rating-slider-row">
                <div className="rating-slider-label">
                  <span className="rating-slider-emoji">🚀</span>
                  <span>Potential</span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={ratings.potential}
                  onChange={(e) => setRatings({...ratings, potential: parseInt(e.target.value)})}
                  className="rating-slider-input"
                />
                <span className="rating-slider-value">{ratings.potential}</span>
              </div>
            </div>
            
            <div className="rating-modal-footer">
              <div className="vote-amount-display">
                <span>Vote Amount:</span>
                <span className="vote-amount-value">{voteAmount} SOL</span>
              </div>
              <button 
                className="submit-vote-btn"
                onClick={handleSubmitVoteWithRatings}
                disabled={isVoting}
              >
                {isVoting ? 'Processing...' : `Submit Vote & Ratings`}
              </button>
            </div>
          </div>
        </div>
      )}

      {!publicKey && !votingEnded && (
        <div className="wallet-notice">
          <p>⚠️ Connect your wallet to start voting!</p>
        </div>
      )}

      <div className="vote-info">
        <h3>How Voting Works</h3>
        <ul>
          <li>Each vote costs SOL (you choose the amount)</li>
          <li>Higher SOL contributions count as stronger support</li>
          <li>You can vote multiple times for your favorite design</li>
          <li><strong>Voting is blind - results hidden until voting ends!</strong></li>
          <li>The design with the most total SOL wins!</li>
          <li>All funds go toward production of the winning plushie</li>
        </ul>
      </div>
    </div>
  );
};

export default Vote;
